import { useEffect, useState } from 'react';
import { Alert, Badge, Modal, Spinner, Table } from 'react-bootstrap';
import { money, shortId, dateStr, dateTimeStr } from '../format';
import { getFinanceOrder } from '../api/financeApi';
import { useFinance } from '../financeContext';
import { VERTICAL_LABEL } from '../wording';

const STATUS_VARIANT = { confirmed: 'success', cancelled: 'danger', pending: 'warning' };

const COLLECTION_SOURCE_LABEL = {
  OWNER_CASH: 'Cash to rental owner',
  OWNER_DIRECT_ONLINE: 'Online, direct to rental owner',
  PLATFORM_ONLINE: 'Online, to RentalVerse business account'
};

function formatAddress(addr) {
  if (!addr) return null;
  if (addr.formatted) return addr.formatted;
  if (!addr.line1) return null;
  return [addr.line1, addr.line2, addr.area, addr.city, addr.pincode].filter(Boolean).join(', ');
}

function bookingIdOf(row) {
  return row?.bookingId || row?._id || row?.id || '';
}

export default function OrderDetailsModal({ booking, show, onHide, onOpenExternal, isAdmin }) {
  const { vertical: hostVertical } = useFinance();
  const [full, setFull] = useState(booking);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !booking) return undefined;
    const id = bookingIdOf(booking);
    const rich = booking.customerName && booking.startDate && (booking.rentalAmount != null || booking.totalAmount != null);
    if (rich) {
      setFull(booking);
      setError('');
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setFull(booking);
    setLoading(true);
    setError('');
    getFinanceOrder(id)
      .then((data) => { if (!cancelled) setFull(data); })
      .catch(() => { if (!cancelled) setError('Could not load this order.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [show, booking]);

  if (!booking) return null;
  const b = full || booking;
  const pay = b.paymentSummary || b.payment || null;
  const address = formatAddress(b.deliveryAddress);
  const assigned = b.deliveryAssignment?.assignedTo;

  return (
    <Modal show={show} onHide={onHide} centered size="lg" fullscreen="sm-down">
      <Modal.Header closeButton>
        <Modal.Title as="h5">Order #{shortId(b._id || bookingIdOf(booking))}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <Spinner animation="border" size="sm" role="status" className="mb-2" />}
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        <div className="d-flex flex-wrap gap-1 mb-3">
          {b.status && <Badge bg={STATUS_VARIANT[b.status] || 'secondary'}>{b.status}</Badge>}
          {b.vertical && <Badge bg="light" text="dark" className="border">{b.vertical}</Badge>}
          {(pay?.needsReconciliation || b.payment?.needsReconciliation) && (
            <Badge bg="warning" text="dark">Awaiting finance reconciliation</Badge>
          )}
          {b.depositStatus && b.depositAmount > 0 && (
            <Badge bg="secondary">Deposit: {String(b.depositStatus).replace(/_/g, ' ')}</Badge>
          )}
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <h6 className="text-uppercase text-muted small">Order</h6>
            <div className="small mb-1"><span className="text-muted">Full ID:</span> <span className="font-monospace">{b._id}</span></div>
            {b.itemName && <div className="small mb-1">{b.itemName}</div>}
            {b.startDate && b.endDate && (
              <div className="small mb-1">{dateStr(b.startDate)} – {dateStr(b.endDate)}</div>
            )}
            {b.createdAt && <div className="small text-muted">Booked {dateTimeStr(b.createdAt)}</div>}

            <h6 className="text-uppercase text-muted small mt-3">Customer</h6>
            <div className="small mb-1">{b.customerName || '—'}</div>
            {b.email && <div className="small text-muted">{b.email}</div>}
            {b.phoneNumber && <div className="small text-muted">{b.phoneNumber}</div>}
            {b.notes && <div className="small mt-2">Notes: {b.notes}</div>}

            {address && (
              <>
                <h6 className="text-uppercase text-muted small mt-3">Delivering to</h6>
                <div className="small">{address}</div>
              </>
            )}
            {(b.ownerName || b.ownerEmail) && (
              <>
                <h6 className="text-uppercase text-muted small mt-3">Owner</h6>
                <div className="small">{[b.ownerName, b.ownerEmail, b.ownerPhone].filter(Boolean).join(' · ')}</div>
              </>
            )}
          </div>

          <div className="col-md-6">
            {pay && (
              <>
                <h6 className="text-uppercase text-muted small">Payment</h6>
                {pay.label && <div className="small mb-1">{pay.label}</div>}
                {(pay.amount != null) && <div className="small">Order amount: {money(pay.amount)}</div>}
                {(pay.amountReceived != null) && <div className="small">Received: {money(pay.amountReceived)}</div>}
                {(pay.amountDue > 0) && <div className="small text-warning">Still receivable: {money(pay.amountDue)}</div>}
                {pay.method && <div className="small">Method: {String(pay.method).replace(/_/g, ' ')}</div>}
                {pay.reference && <div className="small">Reference: <span className="font-monospace">{pay.reference}</span></div>}
                {pay.collectionSource && (
                  <div className="small">Collected: {COLLECTION_SOURCE_LABEL[pay.collectionSource] || pay.collectionSource}</div>
                )}
                {pay.receivedAt && <div className="small text-muted">Received {dateTimeStr(pay.receivedAt)}</div>}
              </>
            )}

            <h6 className="text-uppercase text-muted small mt-3">Charges</h6>
            <Table size="sm" className="mb-0">
              <tbody>
                {b.rentalAmount != null && <tr><td>Rental amount</td><td className="text-end">{money(b.rentalAmount)}</td></tr>}
                {b.platformFee != null && <tr><td>Platform fee</td><td className="text-end">{money(b.platformFee)}</td></tr>}
                {b.deliveryCharge != null && <tr><td>Delivery charge</td><td className="text-end">{money(b.deliveryCharge)}</td></tr>}
                {b.depositAmount != null && <tr><td>Security deposit</td><td className="text-end">{money(b.depositAmount)}</td></tr>}
                {b.totalAmount != null && (
                  <tr className="fw-bold border-top"><td>Total payable</td><td className="text-end">{money(b.totalAmount)}</td></tr>
                )}
              </tbody>
            </Table>

            {b.deliveryStatus && <div className="small mt-3">Delivery status: {String(b.deliveryStatus).replace(/_/g, ' ')}</div>}
            {b.returnStatus && <div className="small">Return status: {String(b.returnStatus).replace(/_/g, ' ')}</div>}
            {assigned && (
              <div className="small text-muted">Assigned to {assigned.name || assigned.phone || '—'}</div>
            )}
            {b.deliveryNeedsReassignment && (
              <div className="small mt-1"><Badge bg="warning" text="dark">Needs reassignment</Badge></div>
            )}
          </div>
        </div>

        {/* "Manage in dashboard" jumps back into the host admin app, which is scoped to ONE
            vertical - its booking endpoints are /api/<hostVertical>/bookings/:id. Handing it a
            booking from a different vertical 404s ("Booking not found"). So only offer it when
            this order belongs to the host's vertical; otherwise say where it can be managed. */}
        {isAdmin && onOpenExternal && b.vertical && hostVertical && b.vertical === hostVertical && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary mt-3"
            onClick={() => onOpenExternal('booking', b)}
          >
            Manage in dashboard
          </button>
        )}
        {isAdmin && onOpenExternal && b.vertical && hostVertical && b.vertical !== hostVertical && (
          <div className="small text-muted mt-3">
            Manage this order in the {VERTICAL_LABEL[b.vertical] || b.vertical} admin app — it can’t
            be actioned from the {VERTICAL_LABEL[hostVertical] || hostVertical} dashboard.
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
